// The currency SCOPE picker for a financial report (SM-496).
//
// Different question from <CurrencySelect>, which asks "which currency
// am I about to move". This one asks "which currency is the report I am
// reading denominated in" — and the difference is why it is not a
// filter with an "All currencies" row like <StakeCurrencyFilter>.
//
// A report has totals. Totals are SUMS of int64 minor units, and RED,
// GREEN and GOLD share a wire scale ($1.00 is 10000 in all three, G2)
// without sharing a value — so an "All" option would produce a headline
// figure that is not a number in any currency. The epic forbids it (G8)
// and settlement enforces it: every aggregate read pins to one currency,
// falling back to GOLD when the caller names none. This control makes
// that pin visible and choosable rather than implicit.
//
// GOLD is the initial default for the same reason it is the server's:
// it is the currency every pre-epic charge was backfilled to, so an
// operator who opens the page and changes nothing sees exactly the
// report they saw before this control existed.
//
// All three currencies are offered. Commission is a slice of a fee
// already collected, so it is denominated in the fee's currency — GOLD
// on a CASH book, RED on the GAME buy side, GREEN on the GAME sell
// side — and all three are real answers rather than empty pages.

import { Select } from "@/components/ui/Select";
import { CurrencyMonogram } from "@/components/ui/CurrencyAmount";
import { useCurrencySettings } from "@/components/currency/CurrencyPresentationProvider";
import { useRegistryCurrencyCodes } from "@/components/currency/registryCurrencies";
import { resolveCurrencyPresentation, type CurrencyCode } from "@/currency";
import { CURRENCY_CODES } from "@/lib/currency";

/**
 * The currency an unscoped report is denominated in.
 *
 * Mirrors settlement's `commissionAggregateFilter` pin, deliberately: a
 * page that opened on a different default would show one set of numbers
 * while a hand-written API call showed another.
 */
export const REPORT_CURRENCY_DEFAULT: CurrencyCode = "GOLD";

/**
 * Read a report currency out of untrusted text (a URL search param).
 *
 * Anything unrecognised reads as the DEFAULT rather than as "no scope":
 * an aggregate always has exactly one currency, so there is no third
 * state for a bad value to fall into, and leaving the param unresolved
 * would put the picker and the table in disagreement about what is on
 * screen.
 *
 * The two legacy aliases are folded in at this boundary — the same
 * normalisation settlement does at its own ingress (G3). A bookmark
 * saved when this console still said SPARK keeps working.
 */
export function parseReportCurrency(raw: unknown): CurrencyCode {
  if (typeof raw !== "string") return REPORT_CURRENCY_DEFAULT;
  const code = raw.trim().toUpperCase();
  if (code === "SPARK" || code === "CREDIT") return REPORT_CURRENCY_DEFAULT;
  return (CURRENCY_CODES as readonly string[]).includes(code)
    ? (code as CurrencyCode)
    : REPORT_CURRENCY_DEFAULT;
}

/**
 * The picker. Options carry each currency's CONFIGURED name and mark, so
 * the control reads the same as the amounts underneath it rather than as
 * three wire codes.
 */
export function ReportCurrencySelect({
  value,
  onChange,
  label,
  className,
}: {
  value: CurrencyCode;
  onChange: (next: CurrencyCode) => void;
  label?: string;
  className?: string;
}) {
  const settings = useCurrencySettings();
  const options = useRegistryCurrencyCodes();
  return (
    <Select
      className={className}
      label={label}
      aria-label="Report currency"
      value={value}
      onChange={(e) => onChange(parseReportCurrency(e.target.value))}
      options={options.map((code) => ({
        value: code,
        // Name AND code: the name is what the console shows everywhere
        // else, the code is what a support ticket or an API call says.
        label: `${resolveCurrencyPresentation(code, settings).displayName} (${code})`,
        icon: <CurrencyMonogram currency={code} />,
      }))}
    />
  );
}
